import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  ActivateResponse,
  TotpSetupResponse,
  TwoFactorMethod,
  TwoFactorStatus,
  VerifyResponse,
} from '../types/twofa';

/**
 * Client for the backend's standalone 2FA endpoints (/rest/2fa/*). All calls are
 * authenticated by the existing auth interceptor (Bearer JWT).
 */
@Injectable({ providedIn: 'root' })
export class TwoFactor {
  private http = inject(HttpClient);
  private readonly base = `${environment.apiUrl}/rest/2fa`;

  getStatus(): Observable<TwoFactorStatus> {
    return this.http.get<TwoFactorStatus>(`${this.base}/status`);
  }

  // --- TOTP enrollment ---
  setupTotp(): Observable<TotpSetupResponse> {
    return this.http.post<TotpSetupResponse>(`${this.base}/totp/setup`, {});
  }

  activateTotp(code: string): Observable<ActivateResponse> {
    return this.http.post<ActivateResponse>(`${this.base}/totp/activate`, { code });
  }

  // --- Email OTP enrollment ---
  setupEmail(): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(`${this.base}/email/setup`, {});
  }

  activateEmail(code: string): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(`${this.base}/email/activate`, { code });
  }

  // --- Login-style verification (for trying it out) ---
  sendEmailLoginCode(): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(`${this.base}/email/send`, {});
  }

  verify(method: TwoFactorMethod, code: string): Observable<VerifyResponse> {
    return this.http.post<VerifyResponse>(`${this.base}/verify`, { method, code });
  }

  disable(method: TwoFactorMethod, code: string): Observable<{ status: string; method: string }> {
    return this.http.post<{ status: string; method: string }>(`${this.base}/disable`, { method, code });
  }
}
